import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadBrokerVerificationDecisionData } from '../redux/actions/voucher/broker-verification-actions';
import { isBrokerVerificationActive } from '../utils/broker-verification-utils';
import { buildOutlierInputsFromState, getOutlierFieldIds } from '../utils/broker-verification-outliers';
import { getCommissionAttachmentFiles } from '../utils/commission-agreement-utils';

const NOT_REQUIRED = { status: 'ready', verificationRequired: false };
const REQUIRED = { status: 'ready', verificationRequired: true };
const LOADING = { status: 'loading', verificationRequired: false };
const FAILED = { status: 'failed', verificationRequired: true };

/**
 * Single decision for "does this voucher go through the Voucher Verification step", read by
 * the tab bar, Continue/Back buttons and the Review panel so they can never disagree.
 *
 * - broker verification inactive (flag off, or early invoice): not required
 * - non-lease deals: always required (unchanged behavior)
 * - lease: required when a bounded value is missing/out of range, the fully-executed-lease /
 *   commission-agreement questions are unanswered, or the commission agreement is Yes with no
 *   document on the voucher; also when the bounds request failed
 *
 * `status` is 'loading' until every input is in Redux. The Review page loads them itself;
 * pass `autoLoad` on other pages (e.g. a refresh on the verification tab) to fetch them here.
 */
const useVerificationRequired = ({ autoLoad = false } = {}) => {
  const dispatch = useDispatch();
  const featureFlags = useSelector(state => state.userOptions?.userOptions?.feature_flags);
  const dealType = useSelector(state => state.deal?.deal?.deal_type);
  const deal = useSelector(state => state.deal?.deal);
  const voucher = useSelector(state => state.voucher?.voucher);
  const compData = useSelector(state => state.compData);
  const dealFiles = useSelector(state => state.dealFiles?.files);
  const commercialProperties = useSelector(state => state.voucher?.commercialProperties);
  const boundsState = useSelector(state => state.voucher?.brokerVerificationBounds);

  const active = isBrokerVerificationActive(featureFlags, voucher?.is_early_invoice);
  const isLease = dealType === 'lease';
  const dealId = deal?.id;
  const voucherId = voucher?.id;

  useEffect(() => {
    if (autoLoad && active && isLease && dealId && voucherId) {
      dispatch(loadBrokerVerificationDecisionData(dealId, voucherId));
    }
  }, [autoLoad, active, isLease, dealId, voucherId, dispatch]);

  return useMemo(() => {
    if (!active) return NOT_REQUIRED;
    if (!dealType) return LOADING;
    if (!isLease) return REQUIRED;

    const boundsLoaded = boundsState?.voucherId === voucherId
      && (boundsState.status === 'ready' || boundsState.status === 'failed');
    if (!voucherId || !boundsLoaded) return LOADING;
    if (boundsState.status === 'failed') return FAILED;

    const inputs = buildOutlierInputsFromState({
      deal, voucher, compData, commercialProperties,
    });
    const outliers = getOutlierFieldIds(boundsState.data, inputs.comp, inputs.info, inputs.voucher);
    const unanswered = deal?.is_fully_executed_lease == null || deal?.has_commission_agreement == null;
    const commissionAgreementMissing = deal?.has_commission_agreement === true
      && getCommissionAttachmentFiles(dealFiles, true).length === 0;
    return {
      status: 'ready',
      verificationRequired: outliers.size > 0 || unanswered || commissionAgreementMissing,
    };
  }, [active, dealType, isLease, voucherId, boundsState, deal, voucher, compData, commercialProperties, dealFiles]);
};

export default useVerificationRequired;
